// Stable across web releases; only bump this when the stored format changes.
const PREFIX = 'lift-media-v1-';
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const MAX_BYTES = 64 * 1024 * 1024;
const MAX_IMAGE = 10 * 1024 * 1024;

export class PhotoCache {
  private generation = 0;
  private pending = new Map<string, Promise<string>>();
  private urls = new Map<string, string>();
  private loadedAt = new Map<string, number>();
  private writes: Promise<unknown> = Promise.resolve();

  private serialize<T>(job: () => Promise<T>): Promise<T> {
    const result = this.writes.then(job);
    this.writes = result.catch(() => {});
    return result;
  }

  async clear() {
    this.generation++;
    this.pending.clear();
    for (const url of this.urls.values()) URL.revokeObjectURL(url);
    this.urls.clear();
    this.loadedAt.clear();
    await this.serialize(async () => {
      if (typeof caches === 'undefined') return;
      for (const name of await caches.keys()) if (name.startsWith(PREFIX)) await caches.delete(name);
    }).catch(() => {});
  }

  /** Drops a photo that failed to display so the next get signs and downloads it again. Its blob
   * URL isn't revoked: other tiles may still show it. */
  async forget(owner: string, key: string) {
    const id = `${owner}:${key}`;
    this.urls.delete(id);
    this.loadedAt.delete(id);
    this.pending.delete(id);
    await this.serialize(async () => {
      if (typeof caches === 'undefined') return;
      await (await caches.open(PREFIX + owner)).delete(new Request(`${location.origin}/__media_cache__/${encodeURIComponent(key)}`));
    }).catch(() => {});
  }

  async get(owner: string, key: string, sign: () => Promise<string>): Promise<string> {
    const id = `${owner}:${key}`;
    // This session already holds the photo: reuse its blob URL without touching storage.
    const ready = this.urls.get(id);
    if (ready && Date.now() - (this.loadedAt.get(id) ?? 0) < MAX_AGE) return ready;
    const existing = this.pending.get(id);
    if (existing) return existing;
    const request = this.load(owner, key, sign);
    this.pending.set(id, request);
    try { return await request; }
    finally { if (this.pending.get(id) === request) this.pending.delete(id); }
  }

  private async load(owner: string, key: string, sign: () => Promise<string>): Promise<string> {
    const generation = this.generation;
    const active = () => {
      if (generation !== this.generation) throw new DOMException('Account changed', 'AbortError');
    };
    const localUrl = (blob: Blob) => {
      active();
      const id = `${owner}:${key}`;
      this.loadedAt.set(id, Date.now());
      const existing = this.urls.get(id);
      if (existing) return existing;
      const url = URL.createObjectURL(blob);
      this.urls.set(id, url);
      return url;
    };
    const request = new Request(`${location.origin}/__media_cache__/${encodeURIComponent(key)}`);
    let cache: Cache | undefined;
    try {
      if (typeof caches !== 'undefined') {
        cache = await caches.open(PREFIX + owner);
        const saved = await cache.match(request);
        if (saved && Date.now() - Number(saved.headers.get('x-saved-at')) < MAX_AGE) {
          return localUrl(await saved.blob());
        }
      }
    } catch { /* Storage can be unavailable or evicted; use the normal signed flow. */ }
    active();
    const url = await sign();
    active();
    // Don't force a second image download if durable storage is unavailable.
    if (!cache) return url;
    let blob: Blob;
    try {
      const response = await fetch(url);
      if (!response.ok || !/^image\/(jpeg|png|webp|gif|heic|heif)(;|$)/i.test(response.headers.get('content-type') || '')) return url;
      if (Number(response.headers.get('content-length')) > MAX_IMAGE) return url;
      blob = await response.blob();
      if (!blob.size || blob.size > MAX_IMAGE) return url;
    } catch { active(); return url; }
    active();
    const saved = new Response(blob, { headers: {
      'content-type': blob.type, 'x-saved-at': String(Date.now()), 'x-size': String(blob.size),
    } });
    await this.serialize(async () => {
      active();
      const entries = await cache!.keys();
      let total = blob.size;
      // Newest first; evict expired/oldest entries until the byte budget fits.
      for (const entry of [...entries].reverse()) {
        const response = await cache!.match(entry);
        const size = Number(response?.headers.get('x-size'));
        const expired = Date.now() - Number(response?.headers.get('x-saved-at')) >= MAX_AGE;
        if (entry.url === request.url || expired || !size || total + size > MAX_BYTES) await cache!.delete(entry);
        else total += size;
      }
      active();
      await cache!.put(request, saved);
    }).catch(() => {}); // Quota failures must not break a successfully downloaded photo.
    return localUrl(blob);
  }
}

export const photoCache = new PhotoCache();
